import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { StaticImage } from 'gatsby-plugin-image';
import useUserContext from '../../hooks/use-user-context';
import { createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { containerStyles } from '../../styles/layout';
import { fontWeights } from '../../theme/type';
import { ReactComponent as ISO } from '../../images/icons/iso.svg';
// import aicpaImage from '../../images/aicpa.png';

import FooterMenu from './FooterMenu';
import FooterUtilityMenu from './FooterUtilityMenu';
import SocialLinks from '../SocialLinks';
import TextRenderer from '../TextRenderer';
import VariableText from '../VariableText';
import Link from '../Link';
import { ReactComponent as IconArrowRight } from '../../images/icons/arrow-right.svg';
import { media } from '../../theme/media';

const ArrowStyled = styled(IconArrowRight)`
  display: inline-block;
  color: ${colors.blue['500']};
  margin-left: ${rem(8)};
  transition: transform 200ms ease;
`;

const Container = styled.footer`
  font-size: ${rem(14)};
  background:${colors.white};
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  padding-top: ${rem(56)};
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${rem(40)} ${rem(30)};
  padding-top: ${rem(48)};
  border-top: 1px solid ${colors.gray['200']};
  margin-bottom: ${rem(48)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    border-color: ${colors.gray['700']};
  `}

  ${media('>smedium')} {
    flex-direction: row;
  }
`;

const FooterMenuStyled = styled(FooterMenu)`
  flex-grow: 1;
`;

const CalloutWrapper = styled.div`

`;

const Callout = styled(Link)`
  display: block;
  align-self: start;
  font-size: ${rem(20)};
  font-weight: ${fontWeights.bold};
  line-height: 1.33;
  color: ${colors.blue['800']};
  margin-bottom: ${rem(20)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.white};
  `}

  ${media('>smedium')} {
   max-width: ${rem(220)};
  }

  &:hover ${ArrowStyled} {
    transform: translateX(8px);
  }
`;

const Certs = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(15)};
  color: ${colors.blue['500']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['300']};
  `}
`;

// const Cert = styled.img`
//   max-height: ${rem(70)};
// `;

const MoreInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: ${rem(20)} 0;
  border-top: 1px solid ${colors.gray['200']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    border-top-color: ${colors.gray['700']};
  `}
`;

const Utility = styled.div`
`;

const FooterUtilityMenuStyled = styled(FooterUtilityMenu)`
  padding-top: ${rem(30)};
`;

const Notice = styled.div`
  padding: ${rem(20)} 0px;
  color: ${colors.gray['600']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.gray['400']};
  `}
`;

const year = new Date().getFullYear();

const Footer = () => {
  const data = useStaticQuery(graphql`
    query Footer {
      allContentfulOptionSet(filter: {name: {eq: "footer"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language, region } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const copyrightNotice = options['footer.copyright_notice']
    ?.replace('{{year}}', year);
  const calloutText = options['footer.callout_text'];
  const calloutLink = options['footer.callout_link'];
  const darkMode = useTheme().pageOverrides?.includes('Dark Mode');

  if (region === 'yuan') {
    return null;
  }

  return (
    <Container>
      <ContentWrapper>
        <MenuContainer>
          <FooterMenuStyled />
          <CalloutWrapper>
            <Callout to={calloutLink}>
              <TextRenderer text={calloutText} />
              <ArrowStyled />
            </Callout>
            <Certs>
              <ISO aria-label="ISO/IEC 27001 Certification icon" />
              <StaticImage
                src="../../images/aicpa.png"
                alt="AICPA SOC icon"
                loading="lazy"
                height={70}
              />
            </Certs>
          </CalloutWrapper>
        </MenuContainer>
        <MoreInfo>
          <Utility>
            <FooterUtilityMenuStyled />
            <Notice>
              <TextRenderer text={copyrightNotice} />
              {' '}
              <VariableText type="text" name="pricing-shown-usd" />
              {' '}
              <VariableText type="text" name="pricing-shown-exclude-taxes" />
            </Notice>
          </Utility>
          <SocialLinks
            color={darkMode ? colors.gray['400'] : colors.gray['600']}
            hoverColor={colors.blue['500']}
            size="large"
          />
        </MoreInfo>
      </ContentWrapper>
    </Container>
  );
};

export default Footer;
